import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import usePrevious from 'shared/hooks/usePrevious';

interface UseInfiniteScrollFetchInput {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  hasInitialLoadCompleted: boolean;
  isLoading: boolean;
  fetchNextPage: () => void;
}

interface UseInfiniteScrollFetchOutput {
  isLoadingNextPage: boolean;
  listEndRef: (node?: Element | null | undefined) => void;
}

export const useInfiniteScrollFetch = ({
  containerRef,
  hasInitialLoadCompleted,
  isLoading,
  fetchNextPage,
}: UseInfiniteScrollFetchInput): UseInfiniteScrollFetchOutput => {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const { inView, ref: channelListEndRef } = useInView({
    root: containerRef?.current,
    skip: !hasInitialLoadCompleted,
    rootMargin: '0px 0px 250px 0px',
  });

  const wasInView = usePrevious(inView);

  /**
   * Load the next page when the user scrolls to the bottom
   */
  useEffect(() => {
    if (!wasInView && inView && !isLoading) {
      setIsLoadingNextPage(true);
      fetchNextPage();
    }
    if (wasInView && !inView) {
      setIsLoadingNextPage(false);
    }
  }, [inView, wasInView, isLoading, fetchNextPage]);

  return {
    isLoadingNextPage,
    listEndRef: channelListEndRef,
  };
};
